<template>
    <div class="drag-drop-file-uploader">
        <h3
            v-if="title"
            class="subtitle-1 font-weight-medium mb-1"
        >
            {{ title }}
        </h3>
        <p v-if="subTitle" class="body-2 text--darken-1 mb-2">
            {{ subTitle }}
        </p>
        <div class="drag-drop-file-uploader__block mb-2">
            <div
                class="drag-drop-file-uploader__wrap"
                :style="areaMinStyle"
                role="button"
                tabindex="0"
                @click="openFilePicker"
                @keydown.enter.prevent="openFilePicker"
                @dragenter.prevent="onDragEnter"
                @dragleave="onDragLeave"
                @dragover.prevent="onDragOver"
                @drop.prevent="onDrop"
            >
                <v-sheet
                    class="drag-drop-file-uploader__visual pa-8 text-center"
                    :class="{ 'drag-drop-file-uploader__visual--active': isDragging, 'drag-drop-file-uploader__visual--error': error }"
                    :style="areaMinStyle"
                    elevation="0"
                >
                    <v-icon
                        size="72"
                        color="primary"
                        :class="{ 'drag-drop-file-uploader__icon--pulse': isDragging }"
                    >
                        mdi-cloud-upload
                    </v-icon>
                    <div class="text-h6 primary--text font-weight-bold mt-2">
                        Browse files
                    </div>
                    <div class="body-2 grey--text text--darken-1 mt-1">
                        Click or drag and drop files here (Max file size: {{ maxFileSizeMb }}MB)
                    </div>
                </v-sheet>
            </div>
            <v-file-input
                ref="fileInput"
                v-model="pickedFiles"
                v-bind="fileInputMultipleBind"
                class="drag-drop-file-uploader__hidden-input ma-0"
                hide-details
                flat
                solo
                prepend-icon=""
                :clearable="false"
                :accept="accept"
                :placeholder="''"
                @change="onPickedFilesChange"
            />
        </div>

        <div v-if="files.length > 0" class="mb-4">
            <div
                v-for="(doc, i) in files"
                :key="doc.name + '-' + doc.file.size + '-' + i"
                class="drag-drop-file-uploader__row d-flex align-center px-3 py-2 mb-2"
            >
                <v-icon class="mr-3" color="primary">
                    mdi-folder-outline
                </v-icon>
                <span class="body-2 flex-grow-1 text-truncate">{{ doc.name }}</span>
                <span class="body-2 grey--text text--darken-1 mr-2 flex-shrink-0">{{ doc.size }}</span>
                <v-btn
                    icon
                    small
                    class="flex-shrink-0"
                    aria-label="Remove file"
                    @click="removeFile(doc.name, doc.file.size)"
                >
                    <v-icon color="grey darken-1">
                        mdi-delete
                    </v-icon>
                </v-btn>
            </div>
        </div>
         <p v-if="error" class="caption red--text mb-1">Please attach at least one file.</p>
        <p v-if="hint" class="caption grey--text mb-1">{{ hint }}</p>
    </div>
</template>

<script>
    import { mapActions } from 'vuex'

    const BYTES_PER_KB = 1024
    const BYTES_PER_MB = 1024 * 1024
    const HUNDRED_KB = 100 * BYTES_PER_KB

    function parseAcceptToExtensions (accept) {
        if (!accept || typeof accept !== 'string') return []
        return accept.split(',').map((s) => {
            const t = s.trim().toLowerCase()
            if (!t) return null
            return t.startsWith('.') ? t : `.${t}`
        }).filter(Boolean)
    }

    function dataTransferHasFiles (dt) {
        if (!dt || !dt.types) return false
        const types = dt.types
        if (typeof types.contains === 'function') {
            return types.contains('Files')
        }
        return Array.prototype.indexOf.call(types, 'Files') !== -1
    }

    export default {
        name: 'DragDropFileUploader',
        model: {
            prop: 'value',
            event: 'input'
        },
        props: {
            value: {
                type: Array,
                default: () => []
            },
            accept: {
                type: String,
                default: '.pdf,.doc,.docx,.xls,.xlsx'
            },
            multiple: {
                type: Boolean,
                default: true
            },
            title: {
                type: String,
                default: 'File upload'
            },
            subTitle: {
                type: String,
                default: ''
            },
            hint: {
                type: String,
                default: ''
            },
            minHeightPx: {
                type: Number,
                default: 200
            },
            error: {
                type: Boolean,
                default: false
            },
            maxFileSizeMb: {
                type: Number,
                default: 10.6
            }
        },
        data: () => ({
            pickedFiles: [],
            isDragging: false
        }),
        computed: {
            files () {
                return Array.isArray(this.value) ? this.value : []
            },
            allowedExtensions () {
                return parseAcceptToExtensions(this.accept)
            },
            areaMinStyle () {
                return { minHeight: `${this.minHeightPx}px` }
            },
            fileInputMultipleBind () {
                return this.multiple ? { multiple: true } : {}
            },
            maxFileSizeBytes () {
                return Math.floor(this.maxFileSizeMb * BYTES_PER_MB)
            }
        },
        methods: {
            ...mapActions(['showAlert']),
            openFilePicker () {
                const fi = this.$refs.fileInput
                const input = fi && fi.$refs && fi.$refs.input
                if (input) input.click()
            },
            onDrop (e) {
                this.isDragging = false
                const dt = e.dataTransfer
                if (!dt || !dt.files || !dt.files.length) return
                this.addFilesFromList(Array.from(dt.files))
            },
            onPickedFilesChange (files) {
                const list = Array.isArray(files) ? files : (files ? [files] : [])
                if (!list.length) return
                this.isDragging = false
                this.addFilesFromList(list)
                this.$nextTick(() => {
                    this.pickedFiles = []
                })
            },
            onDragEnter (e) {
                if (!dataTransferHasFiles(e.dataTransfer)) return
                this.isDragging = true
            },
            onDragLeave (e) {
                const wrap = e.currentTarget
                const rel = e.relatedTarget
                if (rel && wrap.contains(rel)) return
                this.isDragging = false
            },
            onDragOver (e) {
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'copy'
                }
            },
            isAcceptedFile (file) {
                const exts = this.allowedExtensions
                if (!exts.length) return true
                const n = file.name.toLowerCase()
                return exts.some(ext => n.endsWith(ext))
            },
            addFilesFromList (newFiles) {
                let next = [...this.files]

                if (!this.multiple) {
                    next = []
                }

                const maxBytes = this.maxFileSizeBytes
                const oversizedNames = []
                const rejectedExtensionNames = []

                for (const file of newFiles) {
                    if (!this.isAcceptedFile(file)) {
                        rejectedExtensionNames.push(file.name)
                        continue
                    }
                    if (file.size > maxBytes) {
                        oversizedNames.push(file.name)
                        continue
                    }
                    const exists = next.some(
                        d => d.name === file.name && d.file.size === file.size
                    )
                    if (exists) continue
                    next.push({
                        name: file.name,
                        size: this.formatFileSize(file.size),
                        file
                    })
                    if (!this.multiple) break
                }

                if (rejectedExtensionNames.length) {
                    const allowedList = this.allowedExtensions.join(', ')
                    const text = rejectedExtensionNames.length === 1
                        ? `"${rejectedExtensionNames[0]}" is not an allowed file type. Allowed types: ${allowedList}.`
                        : `${rejectedExtensionNames.length} file(s) are not an allowed file type. Allowed types: ${allowedList}.`
                    this.showAlert({ text, type: 'error' })
                }

                if (oversizedNames.length) {
                    const limitLabel = `${this.maxFileSizeMb} MB`
                    const text = oversizedNames.length === 1
                        ? `"${oversizedNames[0]}" is larger than the maximum allowed size (${limitLabel}).`
                        : `${oversizedNames.length} file(s) are larger than the maximum allowed size (${limitLabel}).`
                    this.showAlert({ text, type: 'error' })
                }

                this.$emit('input', next)
            },
            removeFile (name, size) {
                const next = this.files.filter(
                    (d) => !(d.name === name && d.file.size === size)
                )
                this.$emit('input', next)
            },
            formatFileSize (bytes) {
                if (bytes < HUNDRED_KB) {
                    const kb = bytes / BYTES_PER_KB
                    const rounded = kb < 10 ? kb.toFixed(1) : Math.round(kb).toString()
                    return `${rounded} KB`
                }
                const mb = bytes / BYTES_PER_MB
                return `${mb.toFixed(2)} MB`
            },
            clear () {
                this.pickedFiles = []
                this.isDragging = false
                this.$emit('input', [])
            }
        }
    }
</script>

<style lang="scss" scoped>
$drag-active-bg: rgba(108, 157, 57, 0.18);
$border-dashed: rgba(0, 0, 0, 0.38);

@keyframes drag-drop-file-uploader-pulse {
    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.1);
    }
}

.drag-drop-file-uploader {
    padding-top: 32px;
    padding-bottom: 32px;

    &__block {
        position: relative;
    }

    &__wrap {
        cursor: pointer;
        outline: none;

        &:focus .drag-drop-file-uploader__visual {
            background-color: $drag-active-bg;
        }
    }

    &__visual {
        border: 2px dashed $border-dashed;
        border-color: $border-dashed;
        border-radius: 8px;
        transition: all 0.15s ease;

        &--active {
            border-color: $border-dashed !important;
            background-color: $drag-active-bg !important;
        }

        &--error {
            border-color: var(--v-error-base, #d32f2f);
            background-color: #ffebee;
        }
    }

    &__icon--pulse {
        animation: drag-drop-file-uploader-pulse 0.7s ease-in-out infinite;
    }

    &__row {
        border-radius: 4px;
        background-color: $drag-active-bg;
    }

    &__hidden-input {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
    }
}
</style>
